import { CareMapState } from '../../caremap/CareMapState';
import { ICareMap, ICareMapSection } from '../../caremap/ICareMap';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { IUploadCompleteInfo } from '../generic-notes/attachments/FileUploader';
import { INoteTarget } from '../generic-notes/INoteTarget';
import { ICaremapNoteResponse } from './NotesClient';


export class CareMapNoteTracker {
    static inject =() => [CareMapState, MixPanelEventEmitter, HistoryViewModel, CommunityModelFactory];
    constructor(
        private careMapState: CareMapState,
        private tracking: MixPanelEventEmitter,
        private history: HistoryViewModel,
        private communityModelFactory: CommunityModelFactory
    ){}

    onNoteViewed = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: this.isCommunity() ? 'Community Post Viewed' : 'Note Viewed',
            getContext: () => this.getNoteTrackingContextV2(note, 'view', trackingFrom)
        });
    };

    onAddedNote = (note: ICaremapNoteResponse, trackingEvent?: string, trackingFrom?: string, trackingTemplateLabel?: string) => {
        this.tracking.trackWithDebounce({
            event: trackingEvent || 'Note Added',
            getContext: () => this.getNoteTrackingContextV2(note, 'add', trackingFrom, trackingTemplateLabel)
        });
    };

    onUpdatedNote = (note: ICaremapNoteResponse, trackingEvent?: string, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: trackingEvent || 'Note Edited',
            getContext: () => this.getNoteTrackingContextV2(note, 'edit', trackingFrom)
        });
    };

    // TODO: don't love being tied to path
    private isCommunity = () => {
        const path = this.history.getCurrentLocation().pathname;
        return path.includes('/community/');
    };

    onDeletedNote = (deletedNote: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: this.isCommunity() ? 'Community Post Deleted' : 'Note Deleted',
            getContext: () => this.getNoteTrackingContextV2(deletedNote, 'delete', trackingFrom)
        });
    };

    onFileUploaded = (fileInfo: IUploadCompleteInfo, noteTarget: INoteTarget) => {
        this.tracking.track(
            `${this.isCommunity() ? 'Community Post' : 'Note'} File Uploaded`,
            () => {
                return {
                    location: this.getNoteTrackingLocation(noteTarget),
                    type: fileInfo.key.name.substring(fileInfo.key.name.lastIndexOf('.')) || ''
                };
            }
        );
    };

    onFileRemoved = (noteTarget: INoteTarget, url: string) => {
        this.tracking.trackWithDebounce({
            event: `${this.isCommunity() ? 'Community Post' : 'Note'} File Removed`,
            getContext: () => {
                return {
                    location: this.getNoteTrackingLocation(noteTarget),
                    type: url.substring(url.lastIndexOf('.')) || ''
                };
            }
        });
    };

    private getNoteTrackingContextV2 = (
        note: ICaremapNoteResponse,
        action: 'view' | 'add' | 'edit' | 'delete',
        from?: string,
        templateLabel?: string
    ) => {
        const community = this.communityModelFactory.forActiveCaremap();
        const activeCaremap = this.careMapState.getActiveCaremap() as ICareMap | undefined;

        if (!activeCaremap){
            throw new Error('expected an active caremap');
        }

        const noteTarget = note.otherAttributes.noteTarget;

        const section = activeCaremap[noteTarget.section as ICareMapSection];
        let node = section;
        if (noteTarget.memberId) {
            node = (section as any).members.find((m: any) => m.id === noteTarget.memberId);
        }
        const type = (node as any).relationshipId || (node as any).relationship || (node as any).roleId || (node as any).role || '';

        // TODO: don't love being tied to path
        const path = this.history.getCurrentLocation().pathname;

        if (!from) {
            from = path.includes('/caremaps/') ? 'Care Map' :
                path.includes('/notes/') ? 'Notes Page' :
                    this.isCommunity() ? 'Community Page' :
                        'Caregiver Widget';
        }

        const recordingExtras: any = {};
        const recording = note.otherAttributes.recording;
        if (recording && action !== 'add') { // view, edit and delete
            recordingExtras['Has Recording'] = true;
            recordingExtras['Has AI Summary'] = recording?.summary ? true : false;
        }

        // Viewed, Added, Edited, Deleteed
        const actionStr = action;
        const actionLabel = `${actionStr[0].toUpperCase()}${actionStr.slice(1)}ed`;

        return {
            type: type,
            location: this.getNoteTrackingLocation(noteTarget),
            hasTitle: note?.title !== '',
            private: note?.isPrivate as boolean,
            'Template Submitted': templateLabel,
            // all public notes are posted to community
            'Posted to Community': !note?.isPrivate,
            '# of attached files': note.attachments.length,
            [`${actionLabel} From`]: from,
            [`${actionLabel} By`]: note.createdBy === community.getOwner().userId ? 'owner' : 'co-owner',
            ...recordingExtras
        };
    };

    private getNoteTrackingLocation = (noteTarget: INoteTarget) => {
        const isPatient = noteTarget.section === 'patient';
        const isCheckin = noteTarget.section === 'checkin';
        const location = `${isPatient ? 'care recipient' : isCheckin ? 'daily log' : noteTarget.section}${noteTarget.memberId ? ' member' : ''}`;
        return location;
    };
}
